import React, {useMemo, useState, useEffect} from 'react';
import MainLayout from "../../layouts/MainLayout";
import { ResponsiveLine } from '@nivo/line';
import {useListKpiTargetsQuery, useListKpiValuesQuery} from "../../apis/kpiApi";
import _ from "lodash";
import {DateTime} from "luxon";
import {calculateTargetQuarterActual} from "../../hooks/kpiHooks";
import { Dropdown } from 'primereact/dropdown';
import chartTimeFilterOptions, {minMaxGraphValue, sortGraphDates} from "../../hooks/chartFilterHooks";
import { Calendar } from 'primereact/calendar';


const CompanyRoadmapPage = () => {
    const {data: kpiTargets, isFetching: isFetchingKpiTargets} = useListKpiTargetsQuery();
    const {data: kpiValues, isFetching: isFetchingKpiValues} = useListKpiValuesQuery();
    const [filterTypeId, setFilterTypeId] = useState(chartTimeFilterOptions.find(e => e.default).id);
    const [filterTypeDetail, setFilterTypeDetail] = useState();
    const [filterStartDate, setFilterStartDate] = useState();
    const [filterEndDate, setFilterEndDate] = useState();
    const [showFilterDates, setShowFilterDates] = useState(false);
    const [graphYRange, setGraphYRange] = useState({min: "auto", max: "auto"});

    const graphs = useMemo(() => {
        if(isFetchingKpiTargets || isFetchingKpiValues) return [];

        const clonedKpis = _.cloneDeep((kpiTargets || [])
            .filter(target => (target.type === "Target" || target.type === "Benchmark") && target.timeFrame !== 'Quarter'));
        const grouped = _.groupBy(clonedKpis, target => target.type + " " + target.name);

        return sortGraphDates(Object.values(grouped)
            .map(groupedKpi => {
                const groupedKpisByYear = _.groupBy(groupedKpi, target => target.year);
                return {
                    graph: groupedKpi[0]?.name,
                    graphData:
                        Object.values(groupedKpisByYear)
                            .map(groupedKpiByYear => {
                                return {
                                    id: "Goal " + groupedKpiByYear[0]?.year,
                                    data: groupedKpiByYear.sort((kpiTarget1, kpiTarget2) => kpiTarget1.deadline - kpiTarget2.deadline)
                                    .flatMap(kpiTarget => {
                                        if (kpiTarget.timeFrame === 'Annual') {
                                            const begin = DateTime.local(kpiTarget.year, 1, 1).startOf('year');
                                            const q1 = DateTime.local(kpiTarget.year, 1, 1).endOf('quarter');
                                            const q2 = q1.plus({quarters: 1}).endOf('quarter');
                                            const q3 = q2.plus({quarters: 1}).endOf('quarter');
                                            const q4 = q3.plus({quarters: 1}).endOf('quarter');

                                            if(kpiTarget.type === 'Target') {
                                                return [{x: begin.toISODate(), y: 0},
                                                    {x: q1.toISODate(), y: kpiTarget.targets[0]},
                                                    {x: q2.toISODate(), y: kpiTarget.targets[1] + kpiTarget.targets[0]},
                                                    {
                                                        x: q3.toISODate(),
                                                        y: kpiTarget.targets[2] + kpiTarget.targets[1] + kpiTarget.targets[0]
                                                    },
                                                    {
                                                        x: q4.toISODate(),
                                                        y: kpiTarget.targets[3] + kpiTarget.targets[2] + kpiTarget.targets[1] + kpiTarget.targets[0]
                                                    }];
                                            }
                                            else {
                                                return [{x: begin.toISODate(), y: kpiTarget.targets[0]},
                                                    {x: q1.toISODate(), y: kpiTarget.targets[0]},
                                                    {x: q2.toISODate(), y: kpiTarget.targets[1]},
                                                    {
                                                        x: q3.toISODate(),
                                                        y: kpiTarget.targets[2]
                                                    },
                                                    {
                                                        x: q4.toISODate(),
                                                        y: kpiTarget.targets[3]
                                                    }];
                                            }
                                        }

                                        return [{x: DateTime.fromMillis(kpiTarget.deadline).toISODate(), y: kpiTarget.targetValue}];
                                    })
                                }
                            })
                            .concat({
                                id: "Actual",
                                data: kpiValues.filter(value => groupedKpi.some(target => target.id === value.target))
                                    .flatMap(value => {
                                        const kpiTarget = groupedKpi.find(target => target.id === value.target);

                                        const begin = DateTime.local(kpiTarget.year, 1, 1).startOf('year');
                                        const q1 = DateTime.local(kpiTarget.year, 1, 1).endOf('quarter');
                                        const q2 = q1.plus({quarters: 1}).endOf('quarter');
                                        const q3 = q2.plus({quarters: 1}).endOf('quarter');
                                        const q4 = q3.plus({quarters: 1}).endOf('quarter');
                                        const now = DateTime.now();

                                        const firstRow = kpiTarget.type === 'Target' ? {x: begin.toISODate(), y: 0} : {x: begin.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 0)}

                                        if(now < DateTime.local(kpiTarget.year, 1, 1))
                                        {
                                            return [firstRow];
                                        }
                                        else if(now <= q1)
                                        {
                                            return [firstRow, {x: now.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 0)}];
                                        }
                                        else if(now <= q2)
                                        {
                                            return [firstRow, {x: q1.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 0)},
                                                {x: now.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 1)}];
                                        }
                                        else if(now <= q3)
                                        {
                                            return [firstRow, {x: q1.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 0)},
                                                {x: q2.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 1)},
                                                {x: now.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 2)}];
                                        }
                                        else if(now <= q4)
                                        {
                                            return [firstRow, {x: q1.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 0)},
                                                {x: q2.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 1)},
                                                {x: q3.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 2)},
                                                {x: now.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 3)}];
                                        }

                                        return [firstRow, {x: q1.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 0)},
                                            {x: q2.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 1)},
                                            {x: q3.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 2)},
                                            {x: q4.toISODate(), y: calculateTargetQuarterActual(kpiTarget, value, 3)}];
                                    })
                            })
                }
            }));
    }, [kpiTargets, isFetchingKpiTargets, isFetchingKpiValues, kpiValues]);



    useEffect(() => {
        const filterObj = chartTimeFilterOptions.find(e => e.id === filterTypeId);
        setFilterTypeDetail(filterObj);
        setShowFilterDates(!filterObj?.fixed);
      }, [filterTypeId, graphs]);


      useEffect(() => {
       setGraphYRange(minMaxGraphValue(graphs, {filterStartDate, filterEndDate}));
      }, [filterTypeId, graphs, filterStartDate, filterEndDate]);


    if(isFetchingKpiTargets || isFetchingKpiValues) {
        return (<></>);
    }

    const updateEndDate = (e) => {
        setFilterEndDate(e.value)
        if(filterStartDate && filterStartDate > e.value) {
            setFilterStartDate(e.value);
        }
    }

    const updateStartDate = (e) => {
        setFilterStartDate(e.value)
        if(filterEndDate && filterEndDate < e.value) {
            setFilterEndDate(e.value);
        }
    }

    return (
        <MainLayout>
            <div className="field col-12 company-roadmap-filter">
            { showFilterDates && <>
                <Calendar value={filterStartDate}  onChange={updateStartDate} placeholder='Start Date' />
                <Calendar value={filterEndDate} onChange={updateEndDate} placeholder='End Date'/>
            </>}
                <Dropdown value={filterTypeId} onChange={(e) => setFilterTypeId(e.value)} options={chartTimeFilterOptions} optionLabel="label" optionValue="id" placeholder="Time filter"  className="w-full md:w-14rem" />
            </div>
            <div className="mobile-horizontal-scrollable">
                <div className="company-roadmap">
                    {(graphs || []).map((graph, index) => {
                        return (
                            <div className="graph-wrapper" key={index}>
                                <div className="graph-title">{graph.graph}</div>
                                <div className="line-graph">
                                    <ResponsiveLine
                                        data={graph.graphData}
                                        indexBy="date"
                                        margin={{ top: 50, right: 110, bottom: 70, left: 60 }}
                                        xScale={{
                                            type: "time",
                                            format: "%Y-%m-%d",
                                            min: filterTypeDetail?.getStartDate({filterStartDate}),
                                            max: filterTypeDetail?.getEndDate({filterEndDate}),
                                            precision: "day",
                                            clamp: true
                                        }}
                                        yScale={{ type: "linear", stacked: false, clamp: true,min: graphYRange.min, max: graphYRange.max}}
                                        axisTop={null}
                                        axisRight={null}
                                        axisBottom={{
                                            orient: "bottom",
                                            tickSize: 5,
                                            format: "%Y-%m-%d",
                                            tickPadding: 5,
                                            tickRotation: -65,
                                            legend: "",
                                            legendOffset: 40,
                                            legendPosition: "middle"
                                        }}
                                        axisLeft={{
                                            orient: "left",
                                            tickSize: 5,
                                            tickPadding: 5,
                                            tickRotation: 0,
                                            legend: "",
                                            legendOffset: -40,
                                            legendPosition: "middle"
                                        }}

                                        colors={['#E8C1A0', '#1F78B4', '#F1E15B', '#E8A838', '#61CDBB', '#97E3D5', '#6A3D9A', '#083C31', '#D9F0D3']}


                                        pointSize={10}
                                        pointColor={{ theme: "background" }}
                                        pointBorderWidth={2}
                                        pointBorderColor={{ from: "serieColor" }}
                                        pointLabel="y"
                                        pointLabelYOffset={-12}
                                        useMesh={true}
                                        legends={[
                                            {
                                                toggleSerie: true,
                                                anchor: "bottom-right",
                                                direction: "column",
                                                justify: false,
                                                translateX: 100,
                                                translateY: 0,
                                                itemsSpacing: 0,
                                                itemDirection: "left-to-right",
                                                itemWidth: 80,
                                                itemHeight: 20,
                                                itemOpacity: 0.75,
                                                symbolSize: 12,
                                                symbolShape: "circle",
                                                symbolBorderColor: "rgba(0, 0, 0, .5)",
                                                effects: [
                                                    {
                                                        on: "hover",
                                                        style: {
                                                            itemBackground: "rgba(0, 0, 0, .03)",
                                                            itemOpacity: 1
                                                        }
                                                    }
                                                ]
                                            }
                                        ]}
                                    />
                                </div>
                            </div>
                        )
                    })}

                </div>
            </div>
        </MainLayout>
    )
}

export default CompanyRoadmapPage;
